import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  getSaPayPalSettings,
  SaPayPalSettings,
  testSaPayPalConnection,
  updateSaPayPalSettings,
} from "../../../helpers/super_admin_ops_helper";

/**
 * SA — Settings PayPal globais (F2 billing USD).
 * Similar ao padrão SES Endpoints: formulário para credenciais + enabled toggle + test button.
 */
const SuperAdminPayPalSettings = () => {
  const { t } = useTranslation();

  const [settings, setSettings] = useState<SaPayPalSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form fields — vazios = não alterados (PATCH só o que mudou).
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState<"sandbox" | "live">("live");
  const [clientId, setClientId] = useState("");
  const [secret, setSecret] = useState("");
  const [webhookId, setWebhookId] = useState("");

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  document.title = t("sa.paypal.documentTitle");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSaPayPalSettings();
      if (!res?.success || !res.data) {
        setError(t("sa.paypal.error.load"));
        return;
      }
      setSettings(res.data);
      setEnabled(res.data.enabled);
      setMode(res.data.mode);
      // Limpar campos sensíveis — não retornar secret/client_id cru.
      setClientId("");
      setSecret("");
      setWebhookId("");
    } catch (e: any) {
      setError(e?.message || t("sa.paypal.error.load"));
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Só envia campos preenchidos — secret/clientId vazios = não alterar.
      const payload: any = { enabled, mode };
      if (clientId.trim()) payload.client_id = clientId.trim();
      if (secret.trim()) payload.secret = secret.trim();
      if (webhookId.trim()) payload.webhook_id = webhookId.trim();

      const res = await updateSaPayPalSettings(payload);
      toast.success(res.message || t("sa.paypal.save.success"), {
        autoClose: 3500,
      });
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("sa.paypal.error.save"), { autoClose: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await testSaPayPalConnection();
      toast.success(res.message || t("sa.paypal.test.success"), {
        autoClose: 4000,
      });
    } catch (e: any) {
      toast.error(e?.message || t("sa.paypal.error.test"), { autoClose: 5000 });
    } finally {
      setTesting(false);
    }
  };

  const canTest = settings?.has_client_id && settings?.has_secret && !testing;
  const busy = saving || testing;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.paypal.pageTitle")}
          pageTitle={t("sa.menu.paypal")}
        />
        <ToastContainer closeButton={false} limit={3} />

        {loading && (
          <Card>
            <CardBody className="placeholder-glow">
              <span className="placeholder col-4 mb-3" />
              <span className="placeholder col-12 mb-2" />
              <span className="placeholder col-8" />
            </CardBody>
          </Card>
        )}

        {!loading && error && (
          <Alert color="danger">{error}</Alert>
        )}

        {!loading && !error && settings && (
          <Row>
            <Col lg={8}>
              <Card>
                <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <h5 className="card-title mb-0">{t("sa.paypal.card.title")}</h5>
                  <div className="d-flex flex-wrap gap-1">
                    <Badge color={settings.enabled ? "success" : "secondary"}>
                      {settings.enabled
                        ? t("sa.paypal.status.enabled")
                        : t("sa.paypal.status.disabled")}
                    </Badge>
                    <Badge color={settings.mode === "live" ? "primary" : "warning"}>
                      {settings.mode === "live"
                        ? t("sa.paypal.mode.live")
                        : t("sa.paypal.mode.sandbox")}
                    </Badge>
                  </div>
                </CardHeader>
                <CardBody>
                  <Alert color="info" className="mb-3">
                    {t("sa.paypal.hint")}
                  </Alert>

                  {/* Webhook URL fixa — copiar para o painel PayPal. */}
                  <div className="mb-3">
                    <Label className="fw-medium">
                      {t("sa.paypal.webhookUrl")}
                    </Label>
                    <div className="d-flex gap-2 align-items-center">
                      <Input
                        readOnly
                        value={settings.webhook_url}
                        className="font-monospace"
                        style={{ fontSize: "0.9rem" }}
                      />
                      <Button
                        color="light"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(settings.webhook_url);
                          toast.info(t("sa.paypal.webhookUrl.copied"), {
                            autoClose: 2000,
                          });
                        }}
                      >
                        <i className="ri-file-copy-line" />
                      </Button>
                    </div>
                    <div className="form-text">{t("sa.paypal.webhookUrl.hint")}</div>
                  </div>

                  <Form onSubmit={handleSave}>
                    {/* Enabled toggle */}
                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <Input
                          id="paypal-enabled"
                          type="switch"
                          checked={enabled}
                          disabled={busy}
                          onChange={(e) => setEnabled(e.target.checked)}
                        />
                        <Label check htmlFor="paypal-enabled" className="ms-1">
                          {t("sa.paypal.enabled")}
                        </Label>
                      </div>
                      <div className="form-text">
                        {t("sa.paypal.enabled.hint")}
                      </div>
                    </div>

                    {/* Mode sandbox | live */}
                    <div className="mb-3">
                      <Label htmlFor="paypal-mode">{t("sa.paypal.mode")}</Label>
                      <Input
                        id="paypal-mode"
                        type="select"
                        value={mode}
                        disabled={busy}
                        onChange={(e) =>
                          setMode(e.target.value as "sandbox" | "live")
                        }
                      >
                        <option value="sandbox">{t("sa.paypal.mode.sandbox")}</option>
                        <option value="live">{t("sa.paypal.mode.live")}</option>
                      </Input>
                      <div className="form-text">{t("sa.paypal.mode.hint")}</div>
                    </div>

                    {/* Client ID */}
                    <div className="mb-3">
                      <Label htmlFor="paypal-client-id">
                        {t("sa.paypal.clientId")}
                      </Label>
                      <Input
                        id="paypal-client-id"
                        type="text"
                        value={clientId}
                        disabled={busy}
                        onChange={(e) => setClientId(e.target.value)}
                        placeholder={
                          settings.has_client_id
                            ? t("sa.paypal.clientId.placeholder", {
                                last4: settings.client_id_last4 || "••••",
                              })
                            : t("sa.paypal.clientId.placeholderEmpty")
                        }
                      />
                      <div className="form-text">{t("sa.paypal.clientId.hint")}</div>
                    </div>

                    {/* Secret */}
                    <div className="mb-3">
                      <Label htmlFor="paypal-secret">{t("sa.paypal.secret")}</Label>
                      <Input
                        id="paypal-secret"
                        type="password"
                        value={secret}
                        disabled={busy}
                        onChange={(e) => setSecret(e.target.value)}
                        placeholder={
                          settings.has_secret
                            ? t("sa.paypal.secret.placeholder")
                            : t("sa.paypal.secret.placeholderEmpty")
                        }
                      />
                      <div className="form-text">{t("sa.paypal.secret.hint")}</div>
                    </div>

                    {/* Webhook ID */}
                    <div className="mb-3">
                      <Label htmlFor="paypal-webhook-id">
                        {t("sa.paypal.webhookId")}
                      </Label>
                      <Input
                        id="paypal-webhook-id"
                        type="text"
                        value={webhookId}
                        disabled={busy}
                        onChange={(e) => setWebhookId(e.target.value)}
                        placeholder={
                          settings.has_webhook_id
                            ? t("sa.paypal.webhookId.placeholder", {
                                last4: settings.webhook_id_last4 || "••••",
                              })
                            : t("sa.paypal.webhookId.placeholderEmpty")
                        }
                      />
                      <div className="form-text">{t("sa.paypal.webhookId.hint")}</div>
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                      <Button color="primary" type="submit" disabled={busy}>
                        {saving ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            {t("sa.paypal.saving")}
                          </>
                        ) : (
                          t("sa.paypal.save")
                        )}
                      </Button>
                      <Button
                        color="success"
                        outline
                        disabled={!canTest}
                        onClick={handleTest}
                      >
                        {testing ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            {t("sa.paypal.testing")}
                          </>
                        ) : (
                          t("sa.paypal.test")
                        )}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default SuperAdminPayPalSettings;
